import { BehaviorSubject, Subject } from 'rxjs';
import { DEFAULT_TRACKER_OPTIONS } from '../constants/default-tracker';
import {
  WizardStepMeta,
  WizardStepTrackerOptions,
  WizardStepTrackerStep,
  WizardStepTrackingStrategy,
  WizardStepTree,
  WizardStepTreeSnapshot,
  WizardStorage,
  WizardTimeTravelData,
  WizardVersion
} from './wizard-step';

export class WizardStepTracker<Data extends object = object> {
  changes = new Subject<WizardStepTrackerStep<Data>[]>();

  stepTreeSnapshot$ = new BehaviorSubject(this.getDefaultSnapshot());

  currentStepIndex = -1;

  version = WizardVersion.V2;

  treeStepGroups$ = new BehaviorSubject<WizardStepTree>(this._stepTree);

  footerProps$ = new BehaviorSubject({});

  data = {};

  complete = false;

  private _steps: WizardStepTrackerStep<Data>[] = [];

  constructor(
    private _stepTree: WizardStepTree,
    private _storageService: WizardStorage<Data>,
    private readonly _options: WizardStepTrackerOptions = { ...DEFAULT_TRACKER_OPTIONS }
  ) {
    this.treeStepGroups$.next(_stepTree);

    this._options = {
      ...DEFAULT_TRACKER_OPTIONS,
      ..._options
    };

    if (this._options.storageKey) {
      const storedData = this._storageService.getStoredData(this._options.storageKey);

      if (storedData && typeof storedData === 'object') {
        this._steps = storedData.steps || [];
        this.data = storedData.data || {};
        this.currentStepIndex = this._steps.length ? this._steps.length - 1 : -1;
        this.version = storedData.version;
      }
    }

    this._updateStepTreeSnapshot();
  }

  getStepData(path: string): Data | object {
    const stepRef = this.getStepRefFromPath(path);

    if (!stepRef) {
      return {};
    }

    return stepRef.data || {};
  }

  saveStepParams(stepMeta: WizardStepMeta<Data>) {
    const stepRef = this.getStepRefFromPath(stepMeta.path);

    if (!stepRef) {
      return;
    }

    stepRef.params = { ...stepRef.params, ...stepMeta.params };

    this._storeTrackingInfo();

    this.changes.next(this._steps);

    this._updateStepTreeSnapshot();
  }

  saveStepData(path: string, data: Data) {
    const stepRef = this.getStepRefFromPath(path);

    if (!stepRef) {
      return;
    }

    stepRef.data = { ...stepRef.data, ...data };

    this._storeTrackingInfo();

    this.changes.next(this._steps);

    this._updateStepTreeSnapshot();
  }

  getStepRefFromPath(path: string): WizardStepTrackerStep<Data> | undefined {
    return this._steps.find(step => {
      return step.path === path;
    });
  }

  pushStep(step: WizardStepTrackerStep<Data>) {
    this._steps.push(step);
  }

  popStep() {
    this._steps.pop();
  }

  getLength(): number {
    return this._steps.length;
  }

  getStepIndexFromPath(path: string): number {
    return this._steps.findIndex(step => step.path === path);
  }

  track(stepMeta: WizardStepMeta, skipCurrent = false) {
    const { path, params, isFinal } = stepMeta;

    /**
     * If current step is skipped, drop it and
     * optionally the steps leading up to the step being tracked
     */
    if (skipCurrent && this.currentStepIndex >= 0) {
      const deleteCount = this.getStepIndexFromPath(path) - this.currentStepIndex;

      this._steps.splice(this.currentStepIndex, Math.max(1, deleteCount));
    }

    const { trackingStrategy } = this._options;

    const index = this.getStepIndexFromPath(path);

    if (index >= 0) {
      if (trackingStrategy === WizardStepTrackingStrategy.DISCARD_ALWAYS) {
        this._splice(index + 1);
      }

      this.currentStepIndex = index;
    } else {
      const positionOfConnectingStep = this._steps.findIndex(step => step.next === path);

      const stepPosition =
        positionOfConnectingStep >= 0 ? positionOfConnectingStep + 1 : this._steps.length;

      this._steps.splice(stepPosition, 0, {
        path,
        params
      });

      this.currentStepIndex = stepPosition;
    }

    this.changes.next(this._steps);
    this._steps[this.currentStepIndex].complete = false;

    if (isFinal) {
      this.markAsComplete();
    }

    this._storeTrackingInfo();

    this._updateStepTreeSnapshot();
  }

  markCurrentStepDirty(stepPath: string | undefined) {
    if (!stepPath) {
      return;
    }

    const stepIndex = this._steps.findIndex(step => step.path === stepPath);

    if (stepIndex < 0) {
      return;
    }

    this._splice(stepIndex + 1);

    this._steps[stepIndex].dirty = true;
  }

  updateStepTitle(path: string, title: string) {
    const stepRef = this.getStepRefFromPath(path);

    if (!stepRef) {
      return;
    }

    stepRef.title = title;

    this.changes.next(this._steps);

    this._storeTrackingInfo();
    this._updateStepTreeSnapshot();
  }

  getNextStep(path: string): WizardStepTrackerStep<Data> | null {
    const stepIndex: number = this.getStepIndexFromPath(path);

    if (stepIndex >= 0 && stepIndex < this._steps.length - 1) {
      return this._steps[stepIndex + 1];
    }

    return null;
  }

  getPrevStep(path: string): WizardStepTrackerStep<Data> | null {
    const stepIndex: number = this.getStepIndexFromPath(path);

    if (stepIndex > 0) {
      return this._steps[stepIndex - 1];
    }

    return null;
  }

  getSteps(): WizardStepTrackerStep<Data>[] {
    return this._steps;
  }

  markAsComplete() {
    this.complete = true;
    this._updateStepTreeSnapshot();
  }

  markStepAsComplete(path: string) {
    const stepRef = this.getStepRefFromPath(path);

    if (stepRef) {
      stepRef.complete = true;
    }

    this._updateStepTreeSnapshot();
  }

  reset() {
    this._steps = [];
    this.currentStepIndex = -1;
    this.complete = false;
    this.data = {};
    /**
     * Only update version when the user lands to initial step.
     */
    this.version = WizardVersion.V2;

    this._updateStepTreeSnapshot();
    this._storeTrackingInfo();
  }

  saveData(key: string | number | symbol, value: unknown) {
    this.data = { ...this.data, [key]: value };
    this._storeTrackingInfo();
  }

  fastTravel(data: WizardTimeTravelData<Data>) {
    this._steps = data.steps;
    this.currentStepIndex = this._steps.length ? this._steps.length - 1 : -1;
    this.data = data.data || {};

    this._storeTrackingInfo();
    this._updateStepTreeSnapshot();
  }

  get currentStep(): WizardStepTrackerStep<Data> | null {
    if (this.currentStepIndex >= 0) {
      return this._steps[this.currentStepIndex];
    }

    return null;
  }

  updateWizardStepTree(stepGroups: WizardStepTree) {
    this._stepTree = stepGroups;
    this.treeStepGroups$.next(stepGroups);

    this._updateStepTreeSnapshot();
  }

  updateWizardStepFooterProps(props: object) {
    this.footerProps$.next(props);
  }

  getDefaultSnapshot(): WizardStepTreeSnapshot {
    if (!this._stepTree) {
      return {
        segment: '',
        children: [],
        complete: false,
        active: true
      };
    }

    const tree = this._createSnapshotFromGroupsInfo(this._stepTree);
    tree.active = true;

    return tree;
  }

  private _splice(start: number, count?: number) {
    this._steps.splice(start, count || this._steps.length);
  }

  private _updateStepTreeSnapshot() {
    const snapshot: WizardStepTreeSnapshot = this.getDefaultSnapshot();
    snapshot.complete = this.complete;

    this._steps.forEach((step, stepIndex) => {
      const pathSegments = step.path.split('/');
      let currentNode = snapshot;

      const currentPathSegments =
        this.currentStepIndex >= 0 ? this._steps[this.currentStepIndex].path.split('/') : [];

      pathSegments.forEach((segment, segmentIndex) => {
        let nodeRef = currentNode.children.find(child => {
          return child.segment === segment;
        });

        if (!nodeRef) {
          nodeRef = {
            segment,
            children: []
          };

          currentNode.children.push(nodeRef);
        }

        nodeRef.complete = stepIndex < this.currentStepIndex ? true : step.complete;
        nodeRef.active = segment === currentPathSegments[segmentIndex];

        currentNode = nodeRef;
      });
    });

    this.stepTreeSnapshot$.next(snapshot);
  }

  private _storeTrackingInfo() {
    const { storageKey } = this._options;

    if (storageKey) {
      this._storageService.store(storageKey, {
        steps: this._steps,
        data: this.data as Data,
        version: this.version
      });
    }
  }

  private _createSnapshotFromGroupsInfo(stepTree: WizardStepTree): WizardStepTreeSnapshot {
    const tree: WizardStepTreeSnapshot = {
      segment: stepTree.for || '',
      children: [],
      complete: false,
      active: false
    };

    stepTree.children.forEach(child => {
      tree.children.push(this._createSnapshotFromGroupsInfo(child));
    });

    return tree;
  }
}
